import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Inbox from "@/pages/Inbox";
import CRM from "@/pages/CRM";
import ContactDetail from "@/pages/ContactDetail";
import Campaigns from "@/pages/Campaigns";
import CalendarPage from "@/pages/Calendar";
import Agent from "@/pages/Agent";
import Templates from "@/pages/Templates";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Knowledge from "@/pages/Knowledge";
import Orders from "@/pages/Orders";
import Payments from "@/pages/Payments";
import Complaints from "@/pages/Complaints";
import Landing from "@/pages/Landing";
import HumanInbox from "@/pages/HumanInbox";
import Preferences from "@/pages/Preferences";
import TrialExpired from "@/pages/TrialExpired";
import AdminPanel from "@/pages/AdminPanel";
import TermsAcceptModal from "@/components/TermsAcceptModal";

function _trialExpired(user) {
  if (!user?.trial_ends_at) return false;
  const end = Date.parse(user.trial_ends_at);
  return !Number.isNaN(end) && end <= Date.now();
}

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-[#71717A] text-sm">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Super admin lives only in the hidden panel — never let them into the regular app
  if (user.is_super_admin) return <Navigate to="/admin-panel" replace />;
  if (_trialExpired(user)) return <Navigate to="/trial-expired" replace />;
  return (
    <Layout>
      {children}
      {!user.accepted_terms_at && <TermsAcceptModal />}
    </Layout>
  );
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-[#71717A] text-sm">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Non-admins see a generic 404-ish redirect to hide the panel's existence
  if (!user.is_super_admin) return <Navigate to="/" replace />;
  return children;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-[#71717A] text-sm">Cargando...</div>;
  if (user?.is_super_admin) return <Navigate to="/admin-panel" replace />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect Google OAuth session_id synchronously during render (before ProtectedRoute runs)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Public><Login /></Public>} />
          <Route path="/register" element={<Public><Register /></Public>} />
          <Route path="/app/dashboard" element={<Private><Dashboard /></Private>} />
          <Route path="/app/inbox" element={<Private><Inbox /></Private>} />
          <Route path="/app/human-inbox" element={<Private><HumanInbox /></Private>} />
          <Route path="/app/crm" element={<Private><CRM /></Private>} />
          <Route path="/app/contacts/:id" element={<Private><ContactDetail /></Private>} />
          <Route path="/app/campaigns" element={<Private><Campaigns /></Private>} />
          <Route path="/app/calendar" element={<Private><CalendarPage /></Private>} />
          <Route path="/app/agent" element={<Private><Agent /></Private>} />
          <Route path="/app/knowledge" element={<Private><Knowledge /></Private>} />
          <Route path="/app/orders" element={<Private><Orders /></Private>} />
          <Route path="/app/payments" element={<Private><Payments /></Private>} />
          <Route path="/app/complaints" element={<Private><Complaints /></Private>} />
          <Route path="/app/templates" element={<Private><Templates /></Private>} />
          <Route path="/app/analytics" element={<Private><Analytics /></Private>} />
          <Route path="/app/settings" element={<Private><Settings /></Private>} />
          <Route path="/app/preferences" element={<Private><Preferences /></Private>} />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/admin-panel" element={<AdminOnly><AdminPanel /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster theme="dark" position="top-right" />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
